import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Typography from '@sharedComponents/Typography';
import cx from 'classnames';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { components } from 'react-select';

import * as style from './style.module.css';

// Si on a des options et pas de valeur sélectionnée, prendre la première option
// Composant Option personnalisé pour afficher l'icône sur la valeur sélectionnée

const InputSelect = ({
    control,
    label = '',
    options = [],
    required = false,
    isError = false,
    onChange,
    value,
    isSearchable = false,
    disabled = false,
}) => {
    const { t } = useTranslation();

    // Si on a des options et pas de valeur par défaut explicite, prendre la première option
    useEffect(() => {
        if (options.length > 0 && !value && onChange) {
            const firstOption = options[0];
            onChange(firstOption);
        }
    }, [options, value, onChange]);

    const handleChange = (selectedOption) => {
        if (onChange) {
            onChange(selectedOption);
        }
    };

    const CustomOption = (props) => {
        const { children, isSelected } = props;
        const hasSubLabel = props.data && props.data.subLabel;

        return (
            <components.Option {...props} className={cx(style.option, { [style.selectedOption]: isSelected })}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        {children}
                        {hasSubLabel && <span className={style.subLabel}> {props.data.subLabel}</span>}
                    </div>
                    {isSelected && <FontAwesomeIcon icon={faCheck} className={style.selectedIcon} />}
                </div>
            </components.Option>
        );
    };

    // Si on a un control (react-hook-form), on utilise Controller (NO USED YET)
    if (control) {
        return (
            <Controller
                name={label}
                control={control}
                defaultValue={options.length > 0 ? options[0] : ''}
                rules={{
                    validate: (value) => {
                        if (required && !value) {
                            return t('This field is required');
                        }
                        return true;
                    },
                }}
                render={({ field }) => (
                    <Select
                        {...field}
                        unstyled
                        maxMenuHeight={160}
                        isSearchable={isSearchable}
                        isDisabled={disabled}
                        classNames={{
                            menu: () => style.menu,
                            control: (state) => {
                                if (disabled) return style.controlDisabled;
                                if (isError) return style.controlError;
                                return state.menuIsOpen ? style.controlOpen : style.control;
                            },
                            menuList: () => style.menuList,
                            option: () => style.option,
                        }}
                        options={options}
                        onChange={(selectedOption) => {
                            field.onChange(selectedOption);
                            handleChange(selectedOption);
                        }}
                        components={{
                            // customise the message when no option / when no option match the search
                            NoOptionsMessage: ({ ...props }) => {
                                return (
                                    <components.NoOptionsMessage {...props}>
                                        <Typography color={'neutral-tertiary'} center>
                                            {t('No options available')}
                                        </Typography>
                                    </components.NoOptionsMessage>
                                );
                            },
                            Option: CustomOption,
                        }}
                    />
                )}
            />
        );
    }

    // Sinon on utilise Select directement sans react-hook-form
    return (
        <Select
            value={value}
            onChange={handleChange}
            unstyled
            maxMenuHeight={160}
            isSearchable={isSearchable}
            isDisabled={disabled}
            classNames={{
                menu: () => style.menu,
                control: (state) => {
                    if (disabled) return style.controlDisabled;
                    if (isError) return style.controlError;
                    return state.menuIsOpen ? style.controlOpen : style.control;
                },
                menuList: () => style.menuList,
                option: () => style.option,
            }}
            options={options}
            components={{
                // customise the message when no option / when no option match the search
                NoOptionsMessage: ({ ...props }) => {
                    return (
                        <components.NoOptionsMessage {...props}>
                            <Typography color={'neutral-tertiary'} center>
                                {t('No options available')}
                            </Typography>
                        </components.NoOptionsMessage>
                    );
                },
                Option: CustomOption,
                // customise the display of the selected value to show subLabel if available
                SingleValue: ({ children, ...props }) => {
                    const hasSubLabel = props?.data?.subLabel || null;
                    return (
                        <components.SingleValue {...props}>
                            {children}
                            {hasSubLabel ? <span className={style.subLabel}> {props.data.subLabel}</span> : ''}
                        </components.SingleValue>
                    );
                },
            }}
        />
    );
};

export default InputSelect;
